// src/pages/Items.js
import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  Switch,
} from '@mui/material';
import axiosInstance from '../utils/axiosInstance';

const Items = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    price: '',
    contact: '',
    broker: false,
    verified: false,
    active: true,
    image: null,
    images: [],
    existingImages: [],
  });

  // Fetch items
  const fetchItems = async () => {
    const res = await axiosInstance.get('/items');
    setItems(res.data);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (name === 'image') {
      setForm({ ...form, image: files[0] });
    } else if (name === 'images') {
      setForm({ ...form, images: Array.from(files) });
    } else if (type === 'checkbox') {
      setForm({ ...form, [name]: checked });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // Open dialog for edit or add
  const handleOpen = async (item = null) => {
    if (item) {
      setEditId(item.id);
      const res = await axiosInstance.get(`/items/${item.id}`);
      setForm({
        title: res.data.title,
        description: res.data.description || '',
        location: res.data.location || '',
        price: res.data.price,
        contact: res.data.contact,
        broker: !!res.data.broker,
        verified: !!res.data.verified,
        active: !!res.data.active,
        image: null,
        images: [],
        existingImages: res.data.images || [],
      });
    } else {
      setEditId(null);
      setForm({
        title: '',
        description: '',
        location: '',
        price: '',
        contact: '',
        broker: false,
        verified: false,
        active: true,
        image: null,
        images: [],
        existingImages: [],
      });
    }
    setOpen(true);
  };

  // Add or edit item
  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (key === 'image' && form[key]) {
          formData.append('image', form[key]);
        } else if (!['images', 'existingImages'].includes(key)) {
          formData.append(key, form[key]);
        }
      });

      let itemId = editId;

      if (editId) {
        // Update item details
        await axiosInstance.put(`/items/${editId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // Create new item
        const res = await axiosInstance.post('/items', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        itemId = res.data.itemId;
      }

      // Upload additional images if any
      if (form.images.length > 0) {
        const imageData = new FormData();
        form.images.forEach((img) => imageData.append('images', img));
        await axiosInstance.post(`/items/${itemId}/images`, imageData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setOpen(false);
      fetchItems();
    } catch (err) {
      console.error('Failed to save item:', err);
    }
  };

  // Delete item
  const handleDelete = async (id) => {
    await axiosInstance.delete(`/items/${id}`);
    fetchItems();
  };

  // Delete individual image
  const handleDeleteImage = async (imageId) => {
    try {
      await axiosInstance.delete(`/items/${editId}/images/${imageId}`);
      setForm({
        ...form,
        existingImages: form.existingImages.filter((img) => img.id !== imageId),
      });
    } catch (err) {
      console.error('Failed to delete image:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 1.5 },
    { field: 'location', headerName: 'Location', flex: 1 },
    { field: 'price', headerName: 'Price', width: 100 },
    { field: 'contact', headerName: 'Contact', width: 130 },
    {
      field: 'broker',
      headerName: 'Broker/Agent',
      width: 110,
      renderCell: (params) => (params.value ? 'Broker' : 'Owner'),
    },
    {
      field: 'verified',
      headerName: 'Verified',
      width: 90,
      renderCell: (params) => (params.value ? '✅' : '❌'),
    },
    {
      field: 'active',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (params.value ? '🟢' : '🔴'),
    },
    {
      field: 'image_url',
      headerName: 'Main Image',
      width: 100,
      renderCell: (params) =>
        params.value ? <img src={params.value} alt="Item" style={{ width: 50, borderRadius: 4 }} /> : 'No Image',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <>
          <Button onClick={() => handleOpen(params.row)} size="small">Edit</Button>
          <Button onClick={() => handleDelete(params.row.id)} color="error" size="small">Delete</Button>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <h2>Items Management</h2>
      <Button variant="contained" color="primary" onClick={() => handleOpen()} sx={{ mb: 2 }}>
        Add Item
      </Button>

      <div style={{ height: 500, width: '100%', overflowX: 'auto' }}>
        <DataGrid rows={items} columns={columns} pageSize={5} rowsPerPageOptions={[5]} autoHeight />
      </div>

      {/* Dialog Form */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? 'Edit Item' : 'Add Item'}</DialogTitle>
        <DialogContent>
          <TextField label="Title" name="title" value={form.title} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Description" name="description" value={form.description} onChange={handleChange} fullWidth margin="normal" multiline rows={3} />
          <TextField label="Location" name="location" value={form.location} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Price" name="price" value={form.price} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Contact" name="contact" value={form.contact} onChange={handleChange} fullWidth margin="normal" />

          <FormControlLabel control={<Checkbox checked={form.broker} onChange={handleChange} name="broker" />} label="Broker/Agent" />
          <FormControlLabel control={<Checkbox checked={form.verified} onChange={handleChange} name="verified" />} label="Verified" />
          <FormControlLabel control={<Switch checked={form.active} onChange={handleChange} name="active" />} label="Active" />

          <label style={{ marginTop: '16px', display: 'block' }}>Main Image:</label>
          <input type="file" name="image" accept="image/*" onChange={handleChange} style={{ marginTop: '8px' }} />

          <label style={{ marginTop: '16px', display: 'block' }}>Additional Images:</label>
          <input type="file" name="images" accept="image/*" multiple onChange={handleChange} style={{ marginTop: '8px' }} />

          {form.existingImages.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
              {form.existingImages.map((img) => (
                <div key={img.id} style={{ position: 'relative' }}>
                  <img src={img.image_url} alt="Item" width="80" />
                  <Button size="small" color="error" onClick={() => handleDeleteImage(img.id)}>X</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Items;
